import './Settings.css'
import { useState } from 'react'
import { Box, Button, Chip, CircularProgress, Divider, FormControlLabel, Paper, Radio, RadioGroup, Typography } from '@mui/material'
import { useAuth } from 'context/AuthContext'
import { useTranslation } from 'react-i18next'
import { SETTINGS_UPGRADE_FEATURES } from 'utils/constants'
import ChangePasswordModal from 'Components/ChangePasswordModal/ChangePasswordModal'
import DeleteAccountModal from 'Components/DeleteAccountModal'
import RestoreAccountModal from 'Components/RestoreAccountModal'
import apiClient from 'services/apiService'
import { useToast } from 'context/ToastContext'
import { getDaysLeft } from 'utils'

const Settings = () => {
  const { t } = useTranslation()
  const { user, setNotifications } = useAuth()
  const { showToast } = useToast()
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [restoreAccountOpen, setRestoreAccountOpen] = useState(false)
  const [notifyLoading, setNotifyLoading] = useState(false)

  const notifySignedUp = user?.notifications?.proRelease === true
  const isInDeletion = !!user?.deletedAt

  const handleToggleProNotify = async (value: boolean) => {
    if (notifyLoading) return
    setNotifyLoading(true)
    try {
      const res = await apiClient.patch('/user/notifications', { proRelease: value })
      setNotifications(res.data.notifications)
    } catch {
      showToast('error', t('settings.billing.notify-error'))
    } finally {
      setNotifyLoading(false)
    }
  }

  return (
    <Box className="settings-wrapper">

      <Box className="settings-section">
        <Typography variant="subtitle1" className="settings-section-title" sx={{ color: 'text.primary' }}>
          {t('settings.account.title')}
        </Typography>
        <Divider className="settings-section-divider" />
        <Paper variant="outlined" elevation={0} className="settings-card">
          <Box className="settings-row">
            <Box>
              <Typography variant="subtitle2" className="settings-row-label">
                {t('settings.account.email')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email ?? '—'}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <Box className="settings-row">
            <Box>
              <Typography variant="subtitle2" className="settings-row-label">
                {t('settings.account.password')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.account.password-masked')}
              </Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={() => setChangePasswordOpen(true)}>
              {t('settings.account.reset-password')}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Box className="settings-section">
        <Typography variant="subtitle1" className="settings-section-title" sx={{ color: 'text.primary' }}>
          {t('settings.billing.title')}
        </Typography>
        <Divider className="settings-section-divider" />

        {/* Current plan status */}
        <Paper variant="outlined" elevation={0} className="settings-card settings-card-padded">
          <Box className="settings-billing-header">
            <Typography variant="subtitle2" className="settings-row-label">
              {t('settings.billing.free-plan')}
            </Typography>
            <Chip label="Basic" size="small" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('settings.billing.free-features')}
          </Typography>
        </Paper>

        {/* Pro upsell — separate card */}
        <Paper variant="outlined" elevation={0} className="settings-card settings-card-padded settings-pro-upsell">
          <Typography variant="subtitle2" className="settings-row-label" sx={{ mb: 0.5 }}>
            {t('settings.billing.upgrade-title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('settings.billing.upgrade-subtitle')}
          </Typography>
          <Box className="settings-billing-features" sx={{ mb: 2.5 }}>
            {SETTINGS_UPGRADE_FEATURES.map((key) => (
              <Typography key={key} variant="body2" color="text.secondary">
                {'• '}{t(key)}
              </Typography>
            ))}
          </Box>

          {notifySignedUp ? (
            <Box className="settings-pro-notify-signed">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('settings.billing.notify-already')}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => handleToggleProNotify(false)}
                disabled={notifyLoading}
                className="settings-pro-notify-optout"
              >
                {t('settings.billing.notify-optout')}
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleToggleProNotify(true)}
              disabled={notifyLoading}
              startIcon={notifyLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {t('settings.billing.notify-cta')}
            </Button>
          )}
        </Paper>
      </Box>

      <Box className="settings-section">
        <Typography variant="subtitle1" className="settings-section-title" sx={{ color: 'text.primary' }}>
          {t('settings.privacy.title')}
        </Typography>
        <Divider className="settings-section-divider" />
        <Paper variant="outlined" elevation={0} className="settings-card settings-card-padded">
          <Typography variant="subtitle2" className="settings-row-label" sx={{ mb: 1.5 }}>
            {t('settings.privacy.visibility-title')}
          </Typography>
          <RadioGroup value="public">
            <FormControlLabel
              value="public"
              control={<Radio size="small" />}
              label={<Typography variant="body2">{t('settings.privacy.public')}</Typography>}
            />
            <FormControlLabel
              value="private"
              control={<Radio size="small" disabled />}
              label={
                <Typography variant="body2" color="text.disabled">
                  {t('settings.privacy.private')}
                </Typography>
              }
            />
          </RadioGroup>
        </Paper>
      </Box>

      <Box className="settings-section">
        <Typography variant="subtitle1" color="error" className="settings-section-title" sx={{ color: 'text.primary' }}>
          {t('settings.danger.title')}
        </Typography>
        <Divider className="settings-section-divider settings-danger-divider" sx={{ borderColor: 'error.dark' }} />
        <Paper variant="outlined" elevation={0} sx={{ borderColor: 'error.dark' }}>
          <Box className="settings-danger-card">
            {isInDeletion ? (
              <>
                <Box>
                  <Typography variant="subtitle2" className="settings-row-label">
                    {t('settings.danger.in-deletion-title')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{
                      __html: t('settings.danger.in-deletion-description', {
                        days: getDaysLeft(user.deletedAt as string),
                      }),
                    }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  className="settings-danger-cta"
                  onClick={() => setRestoreAccountOpen(true)}
                >
                  {t('settings.danger.restore-cta')}
                </Button>
              </>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" className="settings-row-label">
                    {t('settings.danger.delete-title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.danger.delete-description')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  className="settings-danger-cta"
                  onClick={() => setDeleteAccountOpen(true)}
                >
                  {t('settings.danger.delete-cta')}
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <DeleteAccountModal
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
      />
      <RestoreAccountModal
        open={restoreAccountOpen}
        onClose={() => setRestoreAccountOpen(false)}
      />

    </Box>
  )
}

export default Settings
